import { createContext, useContext, type ReactNode } from "react";

export type PlayVideoArgs = {
  url: string;
  title: string;
  /** Curated description (Kaltura media-entry description, sermon notes, etc.). */
  description?: string;
  /** Kaltura entry ID — used to fetch /video-summary for an AI recap. */
  videoId?: string;
  /** Optional poster image so the side-stage can paint a placeholder while loading. */
  thumbnailUrl?: string;
  /** Optional seek hint (seconds). Used when opening the side-stage from
   *  a scripture-moment chip on an in-chat sermon card. */
  startTime?: number;
};

export type PlayVideoHandler = (args: PlayVideoArgs) => void;

const VideoActionContext = createContext<PlayVideoHandler | null>(null);

/**
 * Provider for the "open video in side-stage" callback.
 *
 * VideoCard reads this context — when set, the card renders a button
 * that dispatches the handler. When unset (admin chat playground has
 * no side-stage host), VideoCard falls back to opening the video URL
 * in a new tab.
 *
 * Intentionally a single nullable function rather than a richer
 * provider — the iframe widget already owns the side-stage state and
 * just needs a way for nested cards to dispatch into it.
 */
export function VideoActionProvider({
  onPlayVideo,
  children,
}: {
  onPlayVideo: PlayVideoHandler;
  children: ReactNode;
}) {
  return (
    <VideoActionContext.Provider value={onPlayVideo}>
      {children}
    </VideoActionContext.Provider>
  );
}

export function useVideoAction(): PlayVideoHandler | null {
  return useContext(VideoActionContext);
}
